import {
  copyMarketplaceDashboard,
  createProfile,
  duplicateDashboard,
  fetchSlimProfiles,
  importProfile,
  removeProfile,
  setProfileTags,
  updateProfile,
} from "@/api/hooks/profile";
import { createTab, importTab, removeTab } from "@/api/hooks/tab";
import Logger from "@mm/logging";
import type { SlimProfile } from "@mm/metx-workbench.meteomatics.com";
import { type PayloadAction, createSelector, createSlice } from "@reduxjs/toolkit";
import { useSelector } from "react-redux";
import type { RootState } from ".";
import { setTabLocationMarkersEnabled, setTabTitle } from "./TabsState";
const logger = Logger.fromFilename(__filename);

export const PROFILE_FETCH_STATUS = {
  idle: "idle",
  loading: "loading",
  succeeded: "succeeded",
  failed: "failed",
} as const;

export type ProfileFetchStatus = (typeof PROFILE_FETCH_STATUS)[keyof typeof PROFILE_FETCH_STATUS];

export type ProfilesState = {
  profiles: { [id: number]: SlimProfile };
  fetchStatus: ProfileFetchStatus;
};

const initialState: ProfilesState = {
  profiles: {},
  fetchStatus: PROFILE_FETCH_STATUS.idle,
} as ProfilesState;

const profileSlice = createSlice({
  name: "profiles",
  initialState: initialState,
  reducers: {
    setProfile(state, action: PayloadAction<{ id: number; profileUpdate: SlimProfile }>) {
      const { profileUpdate, id } = action.payload;

      const profile = state.profiles[id];

      if (!profile) {
        logger.error("Could not find profile with id", id);
        return;
      }

      const updatedProfile = {
        ...profile,
        ...profileUpdate,
      };

      state.profiles[id] = updatedProfile;
    },
    setProfileTitle(state, action: PayloadAction<{ id: number; title: string }>) {
      const { id, title } = action.payload;

      const profile = state.profiles[id];
      if (!profile) {
        console.error("Could not find profile with id", id);
        return;
      }

      profile.title = title;
    },
    setProfileTabs(state, action: PayloadAction<{ id: number; tabs: SlimProfile["tabs"] }>) {
      const { id, tabs } = action.payload;

      const profile = state.profiles[id];
      if (!profile) {
        logger.error("Could not find profile with id", id);
        return;
      }

      // Keep the rest of the cached profile intact and only replace the tab list.
      profile.tabs = tabs;
    },
    removeTagFromAllProfiles(state, action: PayloadAction<string>) {
      const tagToRemove = action.payload;
      for (const id in state.profiles) {
        const profile = state.profiles[id];
        if (profile?.tags?.length) {
          profile.tags = profile.tags.filter((t) => t !== tagToRemove);
        }
      }
    },
    renameTagInAllProfiles(state, action: PayloadAction<{ oldTag: string; newTag: string }>) {
      const { oldTag, newTag } = action.payload;
      for (const id in state.profiles) {
        const profile = state.profiles[id];
        if (profile?.tags?.length) {
          profile.tags = profile.tags.map((t) => (t === oldTag ? newTag : t));
        }
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSlimProfiles.pending, (state) => {
        state.fetchStatus = PROFILE_FETCH_STATUS.loading;
      })
      .addCase(fetchSlimProfiles.fulfilled, (state, action) => {
        state.fetchStatus = PROFILE_FETCH_STATUS.succeeded;
        const profiles = action.payload;

        // Create a new object for profiles
        const newProfiles: {
          [id: number]: SlimProfile;
        } = {};

        if (profiles) {
          for (const profile of profiles) {
            newProfiles[profile.id] = profile;
          }
        }

        // Assign the new profiles object to state.profiles
        state.profiles = newProfiles;
      })
      .addCase(fetchSlimProfiles.rejected, (state) => {
        state.fetchStatus = PROFILE_FETCH_STATUS.failed;
      });

    builder.addCase(importProfile.fulfilled, (state, action) => {
      const profile = action.payload;
      state.profiles[profile.id] = profile;
    });
    builder.addCase(createProfile.fulfilled, (state, action) => {
      const profile = action.payload;
      state.profiles[profile.id] = profile;
    });
    builder.addCase(updateProfile.fulfilled, (state, action) => {
      const profile = action.payload;
      state.profiles[profile.id] = profile;
    });
    builder.addCase(removeProfile.fulfilled, (state, action) => {
      delete state.profiles[action.payload];
    });

    builder.addCase(createTab.fulfilled, (state, action) => {
      const tab = action.payload;
      const profile = state.profiles[tab.id_profile];
      if (!profile) {
        logger.error("Could not find profile with id", tab.id_profile);
        return;
      }
      if (profile.tabs) {
        profile.tabs.push(tab);
      } else {
        profile.tabs = [tab];
      }
    });
    builder.addCase(importTab.fulfilled, (state, action) => {
      const tab = action.payload;
      const profile = state.profiles[tab.id_profile];
      if (!profile) {
        logger.error("Could not find profile with id", tab.id_profile);
        return;
      }

      const tabBase: SlimProfile["tabs"][number] = {
        id: tab.id,
        id_profile: tab.id_profile,
        time_created: tab.time_created,
        time_updated: tab.time_updated,
        title: tab.title,
        order: tab.order,
        is_favorite: tab.is_favorite,
        show_location_markers_on_map: tab.show_location_markers_on_map,
      };

      const existingTabs = profile.tabs ?? [];
      const updatedTabs = existingTabs
        .filter((existingTab) => existingTab.id !== tab.id)
        .concat(tabBase)
        .sort((a, b) => (a.order ?? Number.MAX_SAFE_INTEGER) - (b.order ?? Number.MAX_SAFE_INTEGER));

      profile.tabs = updatedTabs;
    });
    builder.addCase(setTabTitle, (state, action) => {
      const { profileId, tabId, title } = action.payload;
      const profile = state.profiles[profileId];
      if (!profile?.tabs) {
        return;
      }
      profile.tabs = profile.tabs.map((tab) => {
        if (tab.id === tabId) {
          return { ...tab, title };
        }
        return tab;
      });
    });
    builder.addCase(setTabLocationMarkersEnabled, (state, action) => {
      const { profileId, tabId, enabled } = action.payload;
      const profile = state.profiles[profileId];
      if (!profile?.tabs) {
        return;
      }
      profile.tabs = profile.tabs.map((tab) => {
        if (tab.id === tabId) {
          return { ...tab, show_location_markers_on_map: enabled };
        }
        return tab;
      });
    });
    builder.addCase(setProfileTags.fulfilled, (state, action) => {
      const { profileId, tags } = action.payload;
      const profile = state.profiles[profileId];
      if (!profile) {
        logger.error("Could not find profile with id", profileId);
        return;
      }
      profile.tags = tags;
    });
    builder.addCase(removeTab.fulfilled, (state, action) => {
      const { tabId, profileId } = action.payload;
      state.profiles[profileId].tabs = state.profiles[profileId].tabs.filter((tab) => tab.id !== tabId);
    });
    builder.addCase(duplicateDashboard.fulfilled, (state, action) => {
      const profile = action.payload;
      state.profiles[profile.id] = profile;
    });
    builder.addCase(copyMarketplaceDashboard.fulfilled, (state, action) => {
      const profile = action.payload;
      state.profiles[profile.id] = profile;
    });
  },
});

export function useProfiles() {
  return useSelector(
    createSelector(
      (state: RootState) => state.profile,
      (profileState) => {
        return {
          profiles: profileState.profiles,
          fetchStatus: profileState.fetchStatus,
        };
      },
    ),
  );
}

export function useProfile(id: number) {
  return useSelector((state: RootState) => {
    return state.profile.profiles[id];
  });
}

export const { renameTagInAllProfiles, setProfile, setProfileTabs, setProfileTitle, removeTagFromAllProfiles } =
  profileSlice.actions;

export default profileSlice.reducer;
